import { useLongPress } from './useLongPress'
import type { Category } from '../data/types'

interface Props {
  category: Category
  running: boolean
  half?: boolean
  onSelect: (categoryId: string) => void
  /** Long press: start with a note and tags already attached. */
  onHold?: () => void
}

/**
 * The tap target that defines the app. Idle buttons carry the category colour
 * mixed down into the surface; the running one is the full colour, so which mode
 * is live is unmistakable at a glance and from across the desk. Tapping the live
 * one stops it — nothing has to be running.
 */
export function CategoryButton({ category, running, half = false, onSelect, onHold }: Props) {
  // The colour already says it is running; the caption is better spent saying
  // what a tap will do, since tapping the live button is what stops it.
  const caption = half ? '' : running ? 'stop' : ''
  const press = useLongPress(() => onHold?.())
  return (
    <button
      type="button"
      {...(onHold ? press.handlers : {})}
      onClick={() => {
        // A hold already opened the sheet; don't also switch on the way up.
        if (onHold && press.consumed()) return
        onSelect(category.id)
      }}
      aria-pressed={running}
      style={{ '--c': category.color } as React.CSSProperties}
      className={[
        'group relative flex w-full flex-1 select-none flex-col items-center justify-center',
        'rounded-xl border px-2 text-center',
        'transition-[transform,background-color,border-color] duration-150 ease-[var(--ease-out-quick)]',
        'active:scale-[0.97]',
        half ? 'min-h-[56px] gap-0' : 'min-h-[72px] gap-1',
        running
          ? 'border-[var(--c)] bg-[var(--c)] text-[#0a0b0c]'
          : 'border-[color-mix(in_oklab,var(--c)_34%,transparent)] bg-[color-mix(in_oklab,var(--c)_14%,var(--tg-surface))] text-[color-mix(in_oklab,var(--c)_72%,var(--tg-text))]',
      ].join(' ')}
    >
      <span
        className={[
          half ? 'text-[14px]' : 'text-[17px]',
          'leading-none font-semibold tracking-tight',
        ].join(' ')}
      >
        {category.shortLabel}
      </span>
      {caption ? (
        <span
          className={[
            'text-[11px] leading-none font-medium tracking-wide uppercase',
            running ? 'opacity-70' : 'opacity-45',
          ].join(' ')}
        >
          {caption}
        </span>
      ) : null}
    </button>
  )
}
